import {observer} from "mobx-react";
import {useNavigate, useSearchParams} from "react-router";
import {Page, PageContentTitle} from "../../ui";
import {FmsShipmentItemList} from "./FmsShipmentItemList";
import {FmsShipmentPackageItemList} from "./FmsShipmentPackageItemList";
import {useState} from "react";
import {
    FmsBatchFinishDialogStore,
    fmsDocumentPrinterStore,
    fmsOverviewStore,
    fmsVlogPrinterStore,
    growlStore,
    isStorageTransferOrInternalProcess,
} from "../../../store";
import {FmsPicklistOverviewList} from "./FmsPicklistOverviewList";
import {FmsPackageFinishBatchDialog} from "./FmsPackageFinishBatchDialog";
import {fmsCustomerStore} from "../../../store/fms/FmsCustomerStore";
import {MultiPrinterSelectDialog, PrinterMultiSelectDialogStore} from "./MultiPrinterSelectDialog";

export type FmsTabType = '' | 'packages' | 'picklist'

interface FmsOverviewPageProps {
    tab?: FmsTabType
}

const OverviewPackageActions = observer(function OverviewPackageActions() {
    const [searchParams] = useSearchParams()
    const [picklistButtonLoading, setPicklistButtonLoading] = useState(false)
    const [shippingButtonLoading, setShippingButtonLoading] = useState(false)
    const [picklistDialogStore] = useState(new PrinterMultiSelectDialogStore())
    const [batchFinishDialogStore] = useState(new FmsBatchFinishDialogStore())
    const navigate = useNavigate()

    const labelPrinterSelect = !isStorageTransferOrInternalProcess(picklistDialogStore.process)

    return <>
        <MultiPrinterSelectDialog
            processSensitive={true}
            shipmentSensitive={true}
            labelPrinterSelect={labelPrinterSelect}
            store={picklistDialogStore}
            title="Alle Picklisten drucken"
            okLabel="Alle Picklisten drucken"
            action={
                async () => {
                    const picklistIds = await fmsOverviewStore.printPicklists({
                        process: picklistDialogStore.process,
                        shipmentTypes: picklistDialogStore.shipmentTypes,
                        documentPrinter: picklistDialogStore.documentPrinter!,
                        vlogPrinter: picklistDialogStore.vlogPrinter!
                    })
                    const picklistCount = picklistIds?.ids.length || 0

                    growlStore.add({
                        message: `Es wurden ${picklistCount} Picklisten gedruckt`,
                        autoclose: true,
                        closable: true
                    })
                }
            }/>
        <FmsPackageFinishBatchDialog
            title={"Alles versenden"}
            okLabel={"Alles versenden"}
            store={batchFinishDialogStore}
            action={async () => {
                const count: number | boolean = await batchFinishDialogStore.submit()
                batchFinishDialogStore.customer = undefined
                batchFinishDialogStore.labelPrinter = undefined
                batchFinishDialogStore.documentPrinter = undefined
                if (count as any === false) {
                    growlStore.add({
                        message: 'Es ist ein Fehler beim Abschluss aufgetreten',
                        closable: true,
                        cls: 'is-danger'
                    })
                } else {
                    growlStore.add({
                        message: `Es wurden ${count} Versandpakete geschlossen`,
                        autoclose: 5000,
                        closable: true,
                        cls: count === 0 ? 'is-warning' : 'is-success'
                    })
                }

                batchFinishDialogStore.dialogOpen = false
            }}
        />
        <div style={{
            display: 'flex'
        }}>
            <div>
                <button
                    disabled={picklistButtonLoading}
                    className={`button is-rounded ${picklistButtonLoading && 'is-loading'}`}
                    onClick={async () => {
                        setPicklistButtonLoading(true)
                        await fmsDocumentPrinterStore.load()
                        await fmsVlogPrinterStore.load()
                        picklistDialogStore.dialogOpen = true
                        setPicklistButtonLoading(false)
                    }}
                >
                    <span className="icon"><span className="material-symbols-outlined">print</span></span>
                    <span>Alle Picklisten</span>
                </button>
            </div>
            <div style={{paddingLeft: '1em'}}>
                <button
                    disabled={shippingButtonLoading}
                    className={`button is-rounded is-warning ${shippingButtonLoading && 'is-loading'}`}
                    onClick={async () => {
                        setShippingButtonLoading(true)
                        const customerId = searchParams.get('customerId') || undefined
                        await Promise.all([
                            fmsCustomerStore.load(),
                            fmsVlogPrinterStore.load(),
                            fmsDocumentPrinterStore.load()
                        ])
                        if (customerId) {
                            const customer = fmsCustomerStore.items.find(e => e.id === customerId)
                            if (customer) {
                                batchFinishDialogStore.customer = customer
                            }
                        }
                        batchFinishDialogStore.dialogOpen = true
                        setShippingButtonLoading(false)
                    }}
                >
                    <span className="icon"><span className="material-symbols-outlined">local_shipping</span></span>
                    <span>Alles versenden</span>
                </button>
            </div>

            <div style={{paddingLeft: '2em'}}>
                <button key={`button-1`} className={"button is-rounded is-primary"}
                        onClick={() => navigate('/fms/create')}>
                    <span className="icon is-small">
                        <span className="material-symbols-outlined">add</span>
                    </span>
                    <span>Neuen Versandauftrag anlegen</span>
                </button>
            </div>
        </div>
    </>
})

const FmsOverviewPage = observer(function FmsOverviewPage({tab}: FmsOverviewPageProps) {
    const navigate = useNavigate()

    return <Page cssSuffix={"fms"}
                 breadcrumb={[
                     {
                         link: `/fms`,
                         label: 'Versand'
                     }
                 ]}
                 action={<OverviewPackageActions/>}
    >
        <PageContentTitle>
            <div style={{display: "flex"}}>
                <div style={{paddingRight: '1em'}}>
                    <button className={`button is-rounded is-small is-info ${!tab ? '' : 'is-outlined'}`}
                            onClick={() => {
                                navigate(`/fms`)
                            }}>Aufträge
                    </button>
                </div>
                <div style={{paddingRight: '1em'}}>
                    <button className={`button is-rounded is-small is-info ${tab === 'packages' ? '' : 'is-outlined'}`}
                            onClick={() => {
                                navigate(`/fms/packages`)
                            }}>Versandpakete
                    </button>
                </div>
                <div style={{paddingRight: '1em'}}>
                    <button className={`button is-rounded is-small is-info ${tab === 'picklist' ? '' : 'is-outlined'}`}
                            onClick={() => {
                                navigate(`/fms/picklist`)
                            }}>Picklisten
                    </button>
                </div>
            </div>
        </PageContentTitle>

        {!tab && <FmsShipmentItemList items={fmsOverviewStore.items} pages={fmsOverviewStore.pages}/>}
        {tab === 'packages' && <FmsShipmentPackageItemList/>}
        {tab === 'picklist' && <FmsPicklistOverviewList/>}
    </Page>
})

export {
    FmsOverviewPage
}
